import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useApp } from '../context/AppContext';
import {
  ArrowUp, ArrowDown, Minus, BarChart3, Users, Briefcase, DollarSign,
  Send, Target, Calculator, TrendingUp, Activity, AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  formatCurrency, formatFullName, resolveClientName, getServiceLabel,
} from '../utils/helpers';
import { leadStatuses, leadStatusFlow } from '../utils/estimateData';
import { inferLeadStatus, deriveQuoteAmount } from '../utils/leadStageHistory';
import {
  RANGE_OPTIONS, resolveRange, sumMetricsInRange, pctDelta,
} from '../utils/analyticsTimeRanges';
import './AnalyticsDashboard.css';

const STALLED_THRESHOLD_OPTIONS = [7, 14, 30];
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// The timestamp the lead has been sitting in its current stage. Prefers the
// most recent stageHistory entry; falls back to leadStatusUpdatedAt or
// createdAt for older records that predate explicit stage tracking.
function lastStageEnteredAt(estimate) {
  const history = Array.isArray(estimate?.stageHistory) ? estimate.stageHistory : [];
  if (history.length > 0) {
    const last = history[history.length - 1];
    if (last?.enteredAt) return last.enteredAt;
  }
  return estimate?.leadStatusUpdatedAt || estimate?.createdAt || estimate?.submittedAt || null;
}

function daysSince(iso, now = Date.now()) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((now - t) / MS_PER_DAY);
}

// Stages considered "open pipeline" — anything that isn't closed-won or
// closed-lost. Used to filter `state.estimates` for the live pipeline cards.
const CLOSED_STAGES = new Set(['won', 'lost']);

function formatPct(value, fractionDigits = 0) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(fractionDigits)}%`;
}

function DeltaBadge({ delta }) {
  if (!delta) return null;
  if (delta.direction === 'none' || delta.value === null) {
    return <span className="ad-delta flat" title="No prior-period baseline"><Minus size={12} />—</span>;
  }
  if (delta.direction === 'flat') {
    return <span className="ad-delta flat"><Minus size={12} />0%</span>;
  }
  const Icon = delta.direction === 'up' ? ArrowUp : ArrowDown;
  return (
    <span className={`ad-delta ${delta.direction}`}>
      <Icon size={12} />
      {formatPct(Math.abs(delta.value))}
    </span>
  );
}

// ===== Segmentation chart helpers =====

// Sum a per-dimension breakdown (bySource / byServiceType) across the daily
// salesMetrics docs that fall within [startKey, endKey]. Returns a sorted array
// of { key, count } so the bar chart can render directly. We always sort by
// `newLeads` count descending; an "unknown" bucket is preserved (the
// aggregation already maps null/empty values to that key).
function sumDimensionNewLeads(metricsDocs, dimField, startKey, endKey) {
  const totals = {};
  if (!Array.isArray(metricsDocs) || !startKey || !endKey) return [];
  for (const m of metricsDocs) {
    const d = m?.date || m?.id;
    if (!d || d < startKey || d > endKey) continue;
    const dim = m?.[dimField];
    if (!dim) continue;
    for (const [key, breakdown] of Object.entries(dim)) {
      const n = Number(breakdown?.newLeads) || 0;
      if (!n) continue;
      totals[key] = (totals[key] || 0) + n;
    }
  }
  return Object.entries(totals)
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count);
}

// Pretty-print a dimension key for display. Sources/service types come in as
// raw enum-ish strings ("google", "deep_clean"); turn underscores into spaces
// and title-case the result. "unknown" stays lower-case to look distinct.
function formatDimLabel(key) {
  if (!key || key === 'unknown') return 'Unknown';
  return String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// 0–7d, 8–14d, 15–30d, 30+d. Order matters: keys are rendered left-to-right
// in the legend and bottom-to-top in the stacked bar.
const AGING_BUCKETS = [
  { key: '0-7',   label: '0–7d',  min: 0,  max: 7,        color: 'var(--sage)' },
  { key: '8-14',  label: '8–14d', min: 8,  max: 14,       color: 'var(--teal)' },
  { key: '15-30', label: '15–30d', min: 15, max: 30,      color: 'var(--gold)' },
  { key: '30+',   label: '30+d',  min: 31, max: Infinity, color: 'var(--red, #dc2626)' },
];

function bucketForDays(days) {
  // Null / unknown ages fall into 30+ so they surface in the dashboard rather
  // than vanishing — better to over-flag than to silently drop.
  if (days == null) return '30+';
  for (const b of AGING_BUCKETS) {
    if (days >= b.min && days <= b.max) return b.key;
  }
  return '30+';
}

// Stages we render columns for in the aging chart — every open stage in the
// canonical flow except `won` (the flow array already excludes `lost`).
const OPEN_AGING_STAGES = leadStatusFlow.filter((s) => s !== 'won');

// ===== Chart components =====

function HorizontalBarChart({ data, total, emptyLabel = 'No data for this range.' }) {
  if (!data || data.length === 0) {
    return <p className="ad-chart-empty">{emptyLabel}</p>;
  }
  const max = data.reduce((m, d) => (d.count > m ? d.count : m), 0) || 1;
  return (
    <div className="ad-hbar">
      {data.map((row) => {
        const pct = (row.count / max) * 100;
        const sharePct = total > 0 ? (row.count / total) * 100 : 0;
        return (
          <div className="ad-hbar-row" key={row.key}>
            <div className="ad-hbar-label" title={formatDimLabel(row.key)}>
              {formatDimLabel(row.key)}
            </div>
            <div className="ad-hbar-track">
              <div className="ad-hbar-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="ad-hbar-count">
              {row.count.toLocaleString()}
              <span className="ad-hbar-share">{sharePct.toFixed(0)}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PipelineAgingChart({ buckets, stages }) {
  // `buckets` shape: { [stageKey]: { '0-7': n, '8-14': n, '15-30': n, '30+': n, total: n } }
  const stageTotals = stages.map((s) => buckets[s]?.total || 0);
  const maxTotal = stageTotals.reduce((m, t) => (t > m ? t : m), 0) || 1;
  const grandTotal = stageTotals.reduce((s, t) => s + t, 0);

  if (grandTotal === 0) {
    return <p className="ad-chart-empty">No open leads to age.</p>;
  }

  return (
    <div className="ad-aging">
      <div className="ad-aging-legend">
        {AGING_BUCKETS.map((b) => (
          <span key={b.key} className="ad-aging-legend-item">
            <span className="ad-aging-swatch" style={{ background: b.color }} />
            {b.label}
          </span>
        ))}
      </div>
      <div className="ad-aging-grid">
        {stages.map((stage) => {
          const col = buckets[stage] || { total: 0 };
          const total = col.total || 0;
          const colHeightPct = (total / maxTotal) * 100;
          return (
            <div className="ad-aging-col" key={stage}>
              <div className="ad-aging-count">{total}</div>
              <div className="ad-aging-bar-wrap">
                <div
                  className="ad-aging-bar"
                  style={{ height: `${colHeightPct}%` }}
                  title={`${leadStatuses[stage]?.label || stage}: ${total}`}
                >
                  {/* Render segments bottom-up so 0–7d sits at the base. */}
                  {AGING_BUCKETS.map((b) => {
                    const n = col[b.key] || 0;
                    if (!n || total === 0) return null;
                    const segPct = (n / total) * 100;
                    return (
                      <div
                        key={b.key}
                        className="ad-aging-seg"
                        style={{ height: `${segPct}%`, background: b.color }}
                        title={`${b.label}: ${n}`}
                      >
                        {segPct >= 14 ? <span>{n}</span> : null}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="ad-aging-stage" title={leadStatuses[stage]?.label || stage}>
                {leadStatuses[stage]?.label || stage}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <section className="ad-chart-card">
      <header className="ad-chart-head">
        <h3>{title}</h3>
        {subtitle && <span className="ad-chart-sub">{subtitle}</span>}
      </header>
      <div className="ad-chart-body">{children}</div>
    </section>
  );
}

function KpiCard({ label, value, sub, delta, icon: Icon, accent }) {
  return (
    <div className={`ad-kpi ${accent || ''}`}>
      <div className="ad-kpi-head">
        <span className="ad-kpi-label">{label}</span>
        {Icon && <Icon size={16} className="ad-kpi-icon" />}
      </div>
      <div className="ad-kpi-value">{value}</div>
      <div className="ad-kpi-foot">
        {delta ? <DeltaBadge delta={delta} /> : <span className="ad-kpi-foot-muted">Live</span>}
        {sub && <span className="ad-kpi-sub">{sub}</span>}
      </div>
    </div>
  );
}

function AnalyticsDashboard() {
  const { state } = useApp();
  const navigate = useNavigate();
  const [preset, setPreset] = useState('mtd');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [metricsDocs, setMetricsDocs] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [stalledThreshold, setStalledThreshold] = useState(7);

  // Subscribe once to the salesMetrics collection. With a trailing-12-month
  // backfill this is at most ~365 small docs; reading the whole collection is
  // simpler than juggling per-day getDoc calls and gives us reactive updates
  // when the onWrite trigger refreshes a day.
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'salesMetrics'),
      (snap) => {
        setMetricsDocs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setMetricsLoading(false);
      },
      (err) => {
        console.error('salesMetrics subscription failed:', err);
        setMetricsLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // Resolve the current + prior period whenever the selector changes.
  const range = useMemo(
    () => resolveRange(preset, { customStart, customEnd }),
    [preset, customStart, customEnd]
  );

  // Sum daily docs into current / prior totals.
  const currentTotals = useMemo(
    () => sumMetricsInRange(metricsDocs, range.current.startKey, range.current.endKey),
    [metricsDocs, range]
  );
  const priorTotals = useMemo(
    () => sumMetricsInRange(metricsDocs, range.prior.startKey, range.prior.endKey),
    [metricsDocs, range]
  );

  // ===== Live "open pipeline" snapshot from local estimates state =====
  // Estimates are already streamed into context via onSnapshot; filtering
  // here is cheap and stays in sync without a separate query.
  const openPipeline = useMemo(() => {
    const open = state.estimates.filter(e => {
      const stage = inferLeadStatus(e);
      return stage && !CLOSED_STAGES.has(stage);
    });
    let value = 0;
    for (const e of open) {
      const v = Number(deriveQuoteAmount(e));
      if (Number.isFinite(v) && v > 0) value += v;
    }
    return { count: open.length, value: Math.round(value * 100) / 100 };
  }, [state.estimates]);

  // ===== Stalled leads =====
  // A lead is "stalled" if it isn't Won/Lost and hasn't moved stages in
  // `stalledThreshold` days. Sorted by quote amount descending so the highest-
  // value at-risk deals surface first — this is the Monday-morning view.
  const stalledLeads = useMemo(() => {
    const now = Date.now();
    const cutoffDays = stalledThreshold;
    const rows = [];
    for (const est of state.estimates) {
      const stage = inferLeadStatus(est);
      if (!stage || stage === 'won' || stage === 'lost') continue;
      const enteredAt = lastStageEnteredAt(est);
      const days = daysSince(enteredAt, now);
      if (days === null || days < cutoffDays) continue;

      const client = est.clientId ? state.clients.find(c => c.id === est.clientId) : null;
      const assigned = est.assignedTo
        ? (state.employees.find(e => e.id === est.assignedTo) || null)
        : null;
      const assignedLabel = assigned ? formatFullName(assigned) : (est.assignedTo || 'Unassigned');

      rows.push({
        id: est.id,
        name: resolveClientName(est, client),
        stage,
        stageLabel: leadStatuses[stage]?.label || stage,
        stageColor: leadStatuses[stage]?.color,
        stageBg: leadStatuses[stage]?.bg,
        days,
        quoteAmount: deriveQuoteAmount(est),
        assignedTo: assignedLabel,
        source: est.source || (est.requiresReview ? 'website' : 'manual'),
        serviceLabel: getServiceLabel(est),
      });
    }
    rows.sort((a, b) => (b.quoteAmount || 0) - (a.quoteAmount || 0));
    return rows;
  }, [state.estimates, state.clients, state.employees, stalledThreshold]);

  // ===== Derived KPIs =====
  const winRate = useMemo(() => {
    const decisions = currentTotals.won + currentTotals.lost;
    return decisions > 0 ? (currentTotals.won / decisions) * 100 : 0;
  }, [currentTotals]);
  const priorWinRate = useMemo(() => {
    const decisions = priorTotals.won + priorTotals.lost;
    return decisions > 0 ? (priorTotals.won / decisions) * 100 : 0;
  }, [priorTotals]);

  const avgQuoteValue = currentTotals.quotesSent > 0
    ? currentTotals.quotesSentValue / currentTotals.quotesSent : 0;
  const priorAvgQuoteValue = priorTotals.quotesSent > 0
    ? priorTotals.quotesSentValue / priorTotals.quotesSent : 0;

  const avgWonValue = currentTotals.won > 0
    ? currentTotals.wonValue / currentTotals.won : 0;
  const priorAvgWonValue = priorTotals.won > 0
    ? priorTotals.wonValue / priorTotals.won : 0;

  const rangeLabel = `${format(range.current.start, 'MMM d')} – ${format(range.current.end, 'MMM d, yyyy')}`;
  const priorLabel = `vs ${format(range.prior.start, 'MMM d')} – ${format(range.prior.end, 'MMM d')}`;

  // ===== Segmentation: leads by source / by service type (from salesMetrics) =====
  const leadsBySource = useMemo(
    () => sumDimensionNewLeads(metricsDocs, 'bySource', range.current.startKey, range.current.endKey),
    [metricsDocs, range]
  );
  const leadsByServiceType = useMemo(
    () => sumDimensionNewLeads(metricsDocs, 'byServiceType', range.current.startKey, range.current.endKey),
    [metricsDocs, range]
  );

  // ===== Pipeline aging (live from open leads) =====
  // For each open lead: figure out which stage it's currently sitting in and
  // how long it's been there, then bucket. We compute `now` once per render so
  // every lead in the same render uses the same reference point.
  const pipelineAging = useMemo(() => {
    const now = Date.now();
    const buckets = {};
    for (const stage of OPEN_AGING_STAGES) {
      buckets[stage] = { '0-7': 0, '8-14': 0, '15-30': 0, '30+': 0, total: 0 };
    }
    for (const lead of state.estimates) {
      const stage = inferLeadStatus(lead);
      if (!stage || CLOSED_STAGES.has(stage)) continue;
      if (!buckets[stage]) continue; // ignore stages we don't render columns for
      const enteredAt = lastStageEnteredAt(lead);
      const days = daysSince(enteredAt, now);
      const bk = bucketForDays(days);
      buckets[stage][bk] += 1;
      buckets[stage].total += 1;
    }
    return buckets;
  }, [state.estimates]);

  const sourceTotal = leadsBySource.reduce((s, r) => s + r.count, 0);
  const serviceTypeTotal = leadsByServiceType.reduce((s, r) => s + r.count, 0);

  return (
    <div className="ad-page">
      <div className="ad-header">
        <div>
          <h1>Sales Analytics</h1>
          <p className="ad-subtitle">{rangeLabel} · {priorLabel}</p>
        </div>
      </div>

      {/* Sticky time-range selector */}
      <div className="ad-range-bar">
        <div className="ad-range-presets">
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={`ad-range-btn ${preset === opt.value ? 'active' : ''}`}
              onClick={() => setPreset(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="ad-range-custom">
            <label>
              <span>Start</span>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
              />
            </label>
            <label>
              <span>End</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
            </label>
          </div>
        )}
      </div>

      {/* KPI grid */}
      <div className="ad-kpi-grid">
        <KpiCard
          label="New Leads"
          value={currentTotals.newLeads.toLocaleString()}
          icon={Users}
          accent="sage"
          delta={pctDelta(currentTotals.newLeads, priorTotals.newLeads)}
        />
        <KpiCard
          label="Open Pipeline"
          value={openPipeline.count.toLocaleString()}
          sub="leads not Won/Lost"
          icon={Briefcase}
          accent="teal"
        />
        <KpiCard
          label="Open Pipeline $"
          value={formatCurrency(openPipeline.value)}
          sub="quoted value"
          icon={DollarSign}
          accent="teal"
        />
        <KpiCard
          label="Quotes Sent"
          value={currentTotals.quotesSent.toLocaleString()}
          icon={Send}
          accent="gold"
          delta={pctDelta(currentTotals.quotesSent, priorTotals.quotesSent)}
        />
        <KpiCard
          label="Quotes Sent $"
          value={formatCurrency(currentTotals.quotesSentValue)}
          icon={DollarSign}
          accent="gold"
          delta={pctDelta(currentTotals.quotesSentValue, priorTotals.quotesSentValue)}
        />
        <KpiCard
          label="Win Rate"
          value={`${winRate.toFixed(1)}%`}
          sub={`${currentTotals.won} won / ${currentTotals.won + currentTotals.lost} closed`}
          icon={Target}
          accent="sage"
          delta={pctDelta(winRate, priorWinRate)}
        />
        <KpiCard
          label="Avg Quote Value"
          value={formatCurrency(avgQuoteValue)}
          icon={Calculator}
          accent="navy"
          delta={pctDelta(avgQuoteValue, priorAvgQuoteValue)}
        />
        <KpiCard
          label="Avg Won-Job Value"
          value={formatCurrency(avgWonValue)}
          icon={TrendingUp}
          accent="navy"
          delta={pctDelta(avgWonValue, priorAvgWonValue)}
        />
      </div>

      {/* Segmentation charts */}
      <div className="ad-chart-grid">
        <ChartCard
          title="Leads by Source"
          subtitle={`${sourceTotal.toLocaleString()} new leads`}
        >
          <HorizontalBarChart data={leadsBySource} total={sourceTotal} />
        </ChartCard>
        <ChartCard
          title="Leads by Service Type"
          subtitle={`${serviceTypeTotal.toLocaleString()} new leads`}
        >
          <HorizontalBarChart data={leadsByServiceType} total={serviceTypeTotal} />
        </ChartCard>
      </div>
      <ChartCard
        title="Pipeline Aging"
        subtitle="Open leads only · time in current stage"
      >
        <PipelineAgingChart buckets={pipelineAging} stages={OPEN_AGING_STAGES} />
      </ChartCard>

      {/* Stalled Leads */}
      <section className="ad-stalled">
        <div className="ad-stalled-head">
          <div className="ad-stalled-title">
            <AlertTriangle size={18} className="ad-stalled-icon" />
            <h2>Stalled Leads</h2>
            <span className="ad-stalled-count">{stalledLeads.length}</span>
          </div>
          <label className="ad-stalled-threshold">
            <span>Stalled after</span>
            <select
              value={stalledThreshold}
              onChange={(e) => setStalledThreshold(Number(e.target.value))}
            >
              {STALLED_THRESHOLD_OPTIONS.map(d => (
                <option key={d} value={d}>{d} days</option>
              ))}
            </select>
          </label>
        </div>

        {stalledLeads.length === 0 ? (
          <div className="ad-stalled-empty">
            No leads stalled for {stalledThreshold}+ days. Nice.
          </div>
        ) : (
          <div className="ad-stalled-tablewrap">
            <table className="ad-stalled-table">
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Stage</th>
                  <th className="num">Days in stage</th>
                  <th className="num">Quote amount</th>
                  <th>Assigned to</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {stalledLeads.map(row => (
                  <tr
                    key={row.id}
                    className="ad-stalled-row"
                    onClick={() => navigate(`/admin/estimates/${row.id}`)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/admin/estimates/${row.id}`);
                      }
                    }}
                  >
                    <td>
                      <div className="ad-stalled-name">{row.name}</div>
                      <div className="ad-stalled-sub">{row.serviceLabel}</div>
                    </td>
                    <td>
                      <span
                        className="ad-stalled-stage"
                        style={{ color: row.stageColor, background: row.stageBg }}
                      >
                        {row.stageLabel}
                      </span>
                    </td>
                    <td className="num ad-stalled-days">{row.days}</td>
                    <td className="num">
                      {row.quoteAmount ? formatCurrency(row.quoteAmount) : '—'}
                    </td>
                    <td>{row.assignedTo}</td>
                    <td className="ad-stalled-source">{row.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {metricsLoading && (
        <p className="ad-loading"><Activity size={14} /> Loading salesMetrics…</p>
      )}
      {!metricsLoading && metricsDocs.length === 0 && (
        <div className="ad-empty">
          <BarChart3 size={32} />
          <h3>No salesMetrics docs yet</h3>
          <p>Run the Phase 2 backfill to populate the daily aggregation collection.</p>
        </div>
      )}
    </div>
  );
}

export default AnalyticsDashboard;
